import { ChangeDetectorRef, Component, EventEmitter, OnInit, Output, OnDestroy, Inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { validateIBAN, validateIBANPortugal } from 'src/app/commons/utils';
import { InstallationSavedData, Mobile, PaymentSavedData, RazonSocial, TipoVia } from 'src/app/interfaces';
import { NewContractPymesService } from 'src/app/services/new-contract-pymes.service';
import { UtilService } from 'src/app/services/util.service';
import { Select } from '@interfaces';
import { AuthService } from 'src/app/auth/auth.service';
import { UtilValidatorsService } from 'src/app/commons/validations-format/util-validators.service';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';

@Component({
  selector: 'app-payments-data',
  templateUrl: './payments-data.component.html',
  styleUrls: [ './payments-data.component.scss' ]
})
export class PaymentsDataComponent implements OnInit, OnDestroy {
  @Output() formValid = new EventEmitter<any>();
  public paymentForm: FormGroup;
  public addressForm: FormGroup;
  public businessSelected = true;
  public installationSelected = false;
  public otherSelected = false;
  public roadTypeList: TipoVia[] = [];
  private roadTypeListRes: any[] = [];
  public defaultValueRoadType = '';
  public townList = [];
  public defaultValueTown = '';
  public autoComplete = false;
  private destroyed$: Subject<boolean> = new Subject<boolean>();
  private addressFormValid = false;
  private paymentFormValid = false;
  private savedDataBusiness: RazonSocial = null;
  private savedDataInstallation: InstallationSavedData = null;
  public dataToShow: any;
  public isUserSpain = true;
  public mobileCountryCodeList: Select[];

  constructor(
    private formBuilder: FormBuilder,
    private newContractService: NewContractPymesService,
    private cdRef: ChangeDetectorRef,
    private utilService: UtilService,
    private authService: AuthService,
    private utilValidatorsService: UtilValidatorsService,
    @Inject(I18NEXT_SERVICE) private i18NextService: ITranslationService,
  ) { }

  ngOnInit(): void {
    this.isUserSpain = this.authService.userData.data.country === 'ES';
    if (this.newContractService.getSavedData().value.payment) {
      this.businessSelected = false;
      this.installationSelected = false;
      this.otherSelected = true;
    }
    this.newContractService.getSavedData().pipe(takeUntil(this.destroyed$)).subscribe(res => {
      if (res) {
        this.savedDataBusiness = res.business;
        this.savedDataInstallation = res.installation;
        if (this.businessSelected) {
          this.dataToShow = { ...this.savedDataBusiness };
        } else {
          this.dataToShow = { ...this.savedDataInstallation };
        }
      }
    });
    this.newContractService.getTipoVia().pipe(takeUntil(this.destroyed$)).subscribe(res => {
      if (res) {
        this.roadTypeList = [ ...this.mapRoadTypeResponse(res.data) ];
        this.roadTypeListRes = [ ...this.mapRoadTypeResponse(res.data) ];
        this.initPaymentForm();
        this.initAddresForm();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public toggleAddress(option): void {
    this.townList = [];
    this.defaultValueTown = '';
    this.addressForm.controls.town.disable();
    switch (option) {
      case 0:
        this.businessSelected = true;
        this.installationSelected = false;
        this.otherSelected = false;
        this.addressForm.reset();
        this.dataToShow = { ...this.savedDataBusiness };
        this.newContractService.setSavedData({ payment: this.mapValuesToSave() });
        if (this.paymentForm.status === 'VALID') {
          this.formValid.emit({ value: true, tab: 3 });
        } else {
          this.formValid.emit({ value: false, tab: 3 });
        }
        break;
      case 1:
        this.businessSelected = false;
        this.installationSelected = true;
        this.otherSelected = false;
        this.addressForm.reset();
        this.dataToShow = { ...this.savedDataInstallation };
        this.newContractService.setSavedData({ payment: this.mapValuesToSave() });
        if (this.paymentForm.status === 'VALID') {
          this.formValid.emit({ value: true, tab: 3 });
        } else {
          this.formValid.emit({ value: false, tab: 3 });
        }
        break;
      case 2:
        this.businessSelected = false;
        this.installationSelected = false;
        this.otherSelected = true;
        this.addressFormValid = false;
        this.defaultValueRoadType = null;
        this.newContractService.setSavedData({ payment: this.mapValuesToSave() });
        this.formValid.emit({ value: false, tab: 3 });
        break;
    }
    this.cdRef.detectChanges();
  }

  public toggleAutocomplete(ev): void {
    this.autoComplete = !this.autoComplete;
    const mobileAndCountryCode: Mobile = this.getMobileAndCountryCode(this.savedDataBusiness.telefonoContactoComercial);
    if (this.autoComplete) {
      this.paymentForm.patchValue({
        name: this.savedDataBusiness.nombreContactoComercial,
        surname: this.savedDataBusiness.apellidosContactoComercial,
        dni: this.savedDataBusiness.dniContactoComercial,
        email: this.savedDataBusiness.mailContactoComercial,
        mobileCountryCode: mobileAndCountryCode.countryCode ? mobileAndCountryCode.countryCode : null,
        mobile: mobileAndCountryCode.mobile
      });
      this.savedDataBusiness.nombreContactoComercial ? this.paymentForm.controls.name.disable() : this.showErrors('name');
      this.savedDataBusiness.apellidosContactoComercial ? this.paymentForm.controls.surname.disable() : this.showErrors('surname');
      this.savedDataBusiness.dniContactoComercial ? this.paymentForm.controls.dni.disable() : this.showErrors('dni');
      this.savedDataBusiness.mailContactoComercial ? this.paymentForm.controls.email.disable() : this.showErrors('email');
      if (this.savedDataBusiness.telefonoContactoComercial) {
        this.paymentForm.controls.mobile.disable();
        this.paymentForm.controls.mobileCountryCode.disable({ emitEvent: false });
      } else {
        this.showErrors('mobile');
      }
    } else {
      this.paymentForm.enable();
      if (!!this.paymentForm.controls.name.value &&
        !!this.paymentForm.controls.surname.value &&
        !!this.paymentForm.controls.dni.value &&
        !!this.paymentForm.controls.email.value &&
        !!this.paymentForm.controls.mobile.value) {
        this.paymentForm.patchValue({
          name: '',
          surname: '',
          dni: '',
          email: '',
          mobile: ''
        });
      }
    }
  }

  private showErrors(control: string): void {
    this.paymentForm.controls[ control ].markAsTouched();
    this.paymentForm.controls[ control ].setErrors({ required: true });
  }

  private initAddresForm(): void {
    const draftDataPayment = this.newContractService.getSavedData().value.payment;
    this.addressForm = this.formBuilder.group({
      roadType: [ { value: '', disabled: false }, Validators.required ],
      roadName: [ draftDataPayment?.nombreDireccion || '', Validators.required ],
      number: [ draftDataPayment?.numeroDireccion || '' ],
      gate: [ draftDataPayment?.portalDireccion || '' ],
      stair: [ draftDataPayment?.escaleraDireccion || '' ],
      floor: [ draftDataPayment?.pisoDireccion || '' ],
      door: [ draftDataPayment?.puertaDireccion || '' ],
      postalCode: [ { value: '', disabled: false } ],
      postalCodePT1: [ { value: '', disabled: false } ],
      postalCodePT2: [ { value: '', disabled: false } ],
      town: [ { value: '', disabled: true }, [ Validators.required, Validators.maxLength(100) ] ],
      province: [ { value: draftDataPayment?.nombreProvinciaDireccion || '', disabled: true }, [ Validators.maxLength(100) ] ]
    });

    this.initPostalCodePT(draftDataPayment);

    this.defaultValueRoadType = this.roadTypeListRes.find(el => el.label === draftDataPayment?.nombreTipoViaDireccion)?.value || null;
    this.addressForm.controls.roadType.patchValue(this.defaultValueRoadType);
    this.addressForm.statusChanges.pipe(takeUntil(this.destroyed$)).subscribe(result => {
      this.newContractService.setSavedData({ payment: this.mapValuesToSave() });
      if (this.otherSelected) {
        if (result === 'VALID' && (this.isValidPostalCode() &&
          !!this.addressForm.controls.town.value && !!this.addressForm.controls.province.value)) {
          this.addressFormValid = true;
          if (this.paymentFormValid) {
            this.formValid.emit({ value: true, tab: 3 });
          } else {
            this.formValid.emit({ value: false, tab: 3 });
          }
        } else {
          this.addressFormValid = false;
          this.formValid.emit({ value: false, tab: 3 });
        }
      } else {
        if (this.paymentFormValid) {
          this.formValid.emit({ value: true, tab: 3 });
        } else {
          this.formValid.emit({ value: false, tab: 3 });
        }
      }
    });

    this.addressForm.controls.postalCode.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
      this.resetCodigoPostal();
      if (value && this.isValidPostalCode()) {
        this.newContractService.getCodigoPostal(this.addressForm.controls.postalCode.value).pipe(takeUntil(this.destroyed$)).subscribe(
          res => {
            if (res.success) {
              this.townList = [ ...this.mapTownListResponse(res.data.poblaciones) ];
              this.addressForm.controls.town.enable();
              this.defaultValueTown = this.townList.find(el =>
                el.value === draftDataPayment?.codigoPoblacionDireccion
              )?.value || this.townList[ 0 ]?.value;
              this.addressForm.patchValue({
                town: this.defaultValueTown,
                province: res.data.nombreProvincia,
              });
            } else {
              this.utilService.toastErrors(res);
            }
          },
          error => {
            this.utilService.toastErrors(error);
          });
      }
    });

    if (draftDataPayment?.codPostalDireccion) {
      this.addressForm.controls.postalCode.patchValue(draftDataPayment.codPostalDireccion);
    }
  }

  private validatePaymentForm = (control: AbstractControl): ValidationErrors | null => {

    const name = control.get('name');
    const surname = control.get('surname');
    const dni = control.get('dni');
    const email = control.get('email');
    const mobile = control.get('mobile');

    if (name.value === '' || name.value === null ||
      surname.value === '' || surname.value === null ||
      dni.value === '' || dni.value === null ||
      email.value === '' || email.value === null ||
      mobile.value === '' || mobile.value === null) {
      return { invalid_PAYMENTFORM: true };
    }
    return null;
  }

  private validateIBANControls(control: AbstractControl): ValidationErrors | null {

    const iban = control.get('iban').value;
    const entity = control.get('entity').value;
    const office = control.get('office').value;
    const dc = control.get('dc').value;
    const account = control.get('account').value;

    if (!validateIBAN(iban + entity + office + dc + account)) {
      return { invalid_IBAN: true };
    }
    return null;

  }

  private initPaymentForm(): void {
    const draftDataPayment = this.newContractService.getSavedData().value.payment;
    this.paymentForm = this.formBuilder.group({
      name: [ '', [ Validators.required, Validators.maxLength(50) ] ],
      surname: [ '', [ Validators.required, Validators.maxLength(50) ] ],
      dni: [ '', [ Validators.required, this.utilValidatorsService.validateDNI ] ],
      email: [ '', [ Validators.required, Validators.pattern(this.utilValidatorsService.emailPattern) ] ],
      mobileCountryCode: [ { value: '', disabled: false }, [] ],
      mobile: [
        { value: '', disabled: !this.isUserSpain },
        [ this.utilValidatorsService.validateMobile, Validators.required, Validators.maxLength(9) ]
      ],
      iban: [ '', this.isUserSpain ? Validators.required : null ],
      entity: [ '', this.isUserSpain ? Validators.required : null ],
      office: [ '', this.isUserSpain ? Validators.required : null ],
      dc: [ '', this.isUserSpain ? Validators.required : null ],
      account: [ '', this.isUserSpain ? Validators.required : null ]
    }, {
      validator: [
        this.validateIBANControls,
        this.validatePaymentForm
      ]
    });

    if (!this.isUserSpain) {
      this.paymentForm.setValidators([
        this.validatePaymentForm
      ]);
      this.paymentForm.updateValueAndValidity();
    }

    this.initMobileCountryCodeConfigPortugal();

    this.paymentForm.get('mobileCountryCode').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(
      value => {
        if (value) {
          this.paymentForm.get('mobile').enable();
          this.mobileCountryCodeChange(value);
        }
      }
    );

    this.paymentForm.statusChanges.pipe(takeUntil(this.destroyed$)).subscribe(result => {
      this.newContractService.setSavedData({ payment: this.mapValuesToSave() });
      if (result === 'VALID') {
        this.paymentFormValid = true;
        if ((this.otherSelected && this.addressFormValid) || !this.otherSelected) {
          this.formValid.emit({ value: true, tab: 3 });
        } else {
          this.formValid.emit({ value: false, tab: 3 });
        }
      } else {
        this.paymentFormValid = false;
        this.formValid.emit({ value: false, tab: 3 });
      }
    });

    if (draftDataPayment) {
      const mobileAndCountryCode: Mobile = this.getMobileAndCountryCode(draftDataPayment.movilFirmante);
      this.paymentForm.patchValue({
        name: draftDataPayment.nombreFirmante || '',
        surname: draftDataPayment.apellidosFirmante || '',
        dni: draftDataPayment.dniFirmante || '',
        email: draftDataPayment.mailFirmante || '',
        mobileCountryCode: mobileAndCountryCode.countryCode || '',
        mobile: mobileAndCountryCode.mobile || '',
        iban: draftDataPayment.iban || '',
        entity: draftDataPayment.entidad || '',
        office: draftDataPayment.sucursal || '',
        dc: draftDataPayment.dc || '',
        account: draftDataPayment.cuenta || ''
      });
    }
  }
  private mapValuesToSave(): any {
    const savedData = this.newContractService.getSavedData().value;
    let nombreTipoVia = null;
    let nombrePoblacion = null;
    let codigoPoblacionDireccion = '';
    if (this.businessSelected) {
      codigoPoblacionDireccion = savedData.business.codigoPoblacionDireccion;
    } else if (this.installationSelected) {
      codigoPoblacionDireccion = savedData.installation.codigoPoblacionDireccion;
    } else {
      codigoPoblacionDireccion = this.addressForm?.value.town || savedData.payment.codigoPoblacionDireccion;
      nombrePoblacion = this.townList.find(el => el.value === this.addressForm?.value.town)?.label || null;
      if (this.addressForm?.value.roadType) {
        nombreTipoVia = this.roadTypeListRes.find(el => el.value === this.addressForm?.value.roadType)?.label || null;
      } else {
        nombreTipoVia = this.roadTypeListRes.find(el => el.label === savedData.payment.nombreTipoViaDireccion)?.label || null;
      }
    }
    const movilFirmanteValue = this.isUserSpain
      ? this.paymentForm.controls.mobile.value
      : this.paymentForm.controls.mobileCountryCode.value + this.paymentForm.controls.mobile.value;
    return {
      contactoCRM: savedData.contactoCRM,
      idBorrador: savedData.idBorrador,
      codidoInstalacion: savedData.codigoInstalacion,
      codigoCampana: savedData.analysis.campaign,
      nombreNivel: savedData.analysis.levelName || savedData.analysis.level,
      nombreTipoViaDireccion: this.otherSelected ? nombreTipoVia : this.dataToShow?.nombreTipoViaDireccion,
      nombreDireccion: this.otherSelected ?
        (this.addressForm?.value.roadName || savedData.payment.nombreDireccion) :
        this.dataToShow?.nombreDireccion,
      numeroDireccion: this.otherSelected ?
        (this.addressForm?.value.number || savedData.payment.numeroDireccion) :
        this.dataToShow?.numeroDireccion || null,
      portalDireccion: this.otherSelected ?
        (this.addressForm?.value.gate || savedData.payment.portalDireccion) :
        this.dataToShow?.portalDireccion || null,
      escaleraDireccion: this.otherSelected ?
        (this.addressForm?.value.stair || savedData.payment.escaleraDireccion) :
        this.dataToShow?.escaleraDireccion || null,
      pisoDireccion: this.otherSelected ?
        (this.addressForm?.value.floor || savedData.payment.pisoDireccion) :
        this.dataToShow?.pisoDireccion || null,
      puertaDireccion: this.otherSelected ?
        (this.addressForm?.value.door || savedData.payment.puertaDireccion) :
        this.dataToShow?.puertaDireccion || null,
      codPostalDireccion: this.otherSelected ?
        (this.addressForm?.value.postalCode || savedData.payment.codPostalDireccion) :
        this.dataToShow?.codPostalDireccion || null,
      nombrePoblacionDireccion: this.otherSelected ? nombrePoblacion : this.dataToShow?.nombrePoblacionDireccion,
      nombreProvinciaDireccion: this.otherSelected ?
        (this.addressForm?.controls.province.value || savedData.payment.nombrePoblacionDireccion) :
        this.dataToShow?.nombreProvinciaDireccion || null,
      nombreFirmante: this.paymentForm.controls.name.value,
      apellidosFirmante: this.paymentForm.controls.surname.value,
      dniFirmante: this.paymentForm.controls.dni.value,
      movilFirmante: movilFirmanteValue,
      mailFirmante: this.paymentForm.controls.email.value,
      iban: this.paymentForm.value.iban,
      entidad: this.paymentForm.value.entity,
      sucursal: this.paymentForm.value.office,
      dc: this.paymentForm.value.dc,
      cuenta: this.paymentForm.value.account,
      codigoPoblacionDireccion,
      fechaVigenciaFutura: savedData.payment.fechaVigenciaFutura ? savedData.payment.fechaVigenciaFutura : null,
      isMultibanco: savedData.payment.isMultibanco ? savedData.payment.isMultibanco : false
    };
  }

  private mapRoadTypeResponse(values): TipoVia[] {
    const aux = [];
    values.forEach(element => {
      aux.push({ label: element.nombreTipoVia, value: element.idTipoVia });
    });
    return aux;
  }

  private mapTownListResponse(values): Select[] {
    const aux = [];
    values.forEach(element => {
      aux.push({ label: element.nombrePoblacion, value: element.codigoPoblacion });
    });
    return aux;
  }

  private resetCodigoPostal(): void {
    this.townList = [];
    this.addressForm.controls.town.setValue(null);
    this.addressForm.controls.town.disable();
    this.addressForm.controls.province.setValue(null);
  }

  public onPasteIBAN(event: ClipboardEvent): void {
    const pasteIBAN: string = event.clipboardData.getData('text').replace(/\s/g, '');
    if (pasteIBAN.length === 24) {
      this.paymentForm.patchValue({
        iban: pasteIBAN.slice(0, 4),
        entity: pasteIBAN.slice(4, 8),
        office: pasteIBAN.slice(8, 12),
        dc: pasteIBAN.slice(12, 14),
        account: pasteIBAN.slice(14, 24)
      });
    }
  }

  public bankAccountKeyUp(
    event: KeyboardEvent,
    previousInputId: string | null,
    currentInputId: string,
    nextInputId: string | null
  ): void {
    const previousInput = this.getInputElement(previousInputId);
    const currentInput = this.getInputElement(currentInputId);
    const nextInput = this.getInputElement(nextInputId);
    if (!event.ctrlKey && event.key !== 'Control') {
      if (currentInput.selectionStart === currentInput.maxLength && nextInput) {
        nextInput.focus();
        nextInput.setSelectionRange(0, 0);
      } else if (
        (event.key === 'Backspace' || event.key === 'ArrowLeft')
        && currentInput.selectionStart === 0
        && previousInput
      ) {
        previousInput.focus();
      }
    }
  }

  private getInputElement(id: string): HTMLInputElement | null {
    return id ? (document.getElementById(id) as HTMLInputElement) : null;
  }

  public mobileCountryCodeChange(value: string): void {
    if (value === '0034') {
      this.paymentForm.get('mobile').setValidators(
        [ this.utilValidatorsService.validateMobile, Validators.required, Validators.maxLength(9) ]
      );
    } else if (value === '00351') {
      this.paymentForm.get('mobile').setValidators(
        [ this.utilValidatorsService.validateMobilePT, Validators.required, Validators.maxLength(9) ]
      );
    }
    this.paymentForm.get('mobile').updateValueAndValidity({ onlySelf: true });
  }

  private getMobileAndCountryCode(value: string | null): Mobile {
    const result: Mobile = { mobile: value };
    if (value) {
      if (!this.isUserSpain && value?.length > 9) {
        const countryCode = value.slice(0, value.length - 9);
        const validCountryCode = this.mobileCountryCodeList.find(code => code.value === countryCode);
        if (validCountryCode) {
          result.countryCode = countryCode;
        }
      }
      result.mobile = value.slice(value.length - 9, value.length);
    }

    return result;
  }

  private initMobileCountryCodeConfigPortugal(): void {
    if (!this.isUserSpain) {
      this.paymentForm.get('mobileCountryCode').setValidators(Validators.required);
      this.initMobileCountryCodeList();
    }
  }

  private initMobileCountryCodeList(): void {
    this.i18NextService.events.languageChanged.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.mobileCountryCodeList = this.utilService.getListCountryMobile();
    });
  }

  private initPostalCodePT(draftDataPayment: PaymentSavedData): void {
    if (!this.isUserSpain) {
      this.addressForm.get('postalCodePT1').setValidators([ Validators.required, Validators.maxLength(4) ]);
      this.addressForm.get('postalCodePT2').setValidators([ Validators.required, Validators.maxLength(3) ]);
      this.addressForm.get('postalCode').setValidators([
        Validators.required,
        Validators.pattern(this.utilValidatorsService.postalCodePTPattern)
      ]);

      if (draftDataPayment) {
        this.addressForm.get('postalCodePT1').patchValue(draftDataPayment?.codPostalDireccion?.slice(0, 4));
        this.addressForm.get('postalCodePT2').patchValue(draftDataPayment?.codPostalDireccion?.slice(5, 8));
      }

      this.addressForm.get('postalCodePT1').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
        this.addressForm.get('postalCode').setValue(value + '-' + this.addressForm.get('postalCodePT2').value);
      });
      this.addressForm.get('postalCodePT2').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
        this.addressForm.get('postalCode').setValue(this.addressForm.get('postalCodePT1').value + '-' + value);
      });
    } else {
      this.addressForm.get('postalCode').setValidators([ Validators.required ]);
    }
  }

  private isValidPostalCode(): boolean {
    if (this.isUserSpain) {
      return this.addressForm.controls.postalCode.value?.length > 4;
    } else {
      return this.addressForm.get('postalCode').valid;
    }
  }
}
